import { createSlice } from '@reduxjs/toolkit';
import {
    fetchContacts,
    addContact,
    updateContact,
    deleteContact,
    addTags,
    removeTags,
    addNotes,
    removeNotes,
    updateNote,
    fetchChatList,
    upsertContacts,
    assignContact
} from './contactThunk.js';

const contactSlice = createSlice({
    name: 'contact',
    initialState: {
        contacts: [],
        chatList: [],
        selectedContact: null, // ✅ ensure consistent with chatSlice
        loading: false,
        error: null,
    },
    reducers: {
        updateChatPreview: (state, action) => {
            const { phone, latestMessage, name } = action.payload;
            let chat = state.chatList.find(c => c.phone === phone);
            if (chat) {
                chat.latestMessage = latestMessage;
            } else {
                const newChat = {
                    phone,
                    name: name || phone,
                    latestMessage,
                    _id: phone,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    tags: [],
                    attributes: [],
                    notes: []
                };
                state.chatList.unshift(newChat);
            }
        },
        setSelectedContact: (state, action) => {
            state.selectedContact = action.payload; // ✅ full contact object
        },
        clearSelectedContact: (state) => {
            state.selectedContact = null;
        },
        clearContacts: (state) => {
            state.contacts = [];
            state.chatList = [];
            state.selectedContact = null;
        },
    },
    extraReducers: (builder) => {
        const handlePending = (state) => {
            state.loading = true;
            state.error = null;
        };

        const handleRejected = (state, action) => {
            state.loading = false;
            state.error = action.payload;
        };

        const updateContactInList = (state, updated) => {
            const idx = state.contacts.findIndex(c => c._id === updated._id);
            if (idx !== -1) state.contacts[idx] = updated;
        };

        builder
            // Upsert Contacts
            .addCase(upsertContacts.pending, handlePending) 
            .addCase(upsertContacts.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(upsertContacts.rejected, handleRejected)

            // Chat List
            .addCase(fetchChatList.pending, handlePending)
            .addCase(fetchChatList.fulfilled, (state, action) => {
                state.loading = false;
                state.chatList = action.payload;
            })
            .addCase(fetchChatList.rejected, handleRejected)

            // Fetch Contacts
            .addCase(fetchContacts.pending, handlePending)
            .addCase(fetchContacts.fulfilled, (state, action) => {
                state.loading = false;
                state.contacts = action.payload;
            })
            .addCase(fetchContacts.rejected, handleRejected)

            // Add Contact
            .addCase(addContact.pending, handlePending)
            .addCase(addContact.fulfilled, (state, action) => {
                state.loading = false;
                state.contacts.push(action.payload);
            })
            .addCase(addContact.rejected, handleRejected)

            // Update Contact
            .addCase(updateContact.pending, handlePending)
            .addCase(updateContact.fulfilled, (state, action) => {
                state.loading = false;
                updateContactInList(state, action.payload);
            })
            .addCase(updateContact.rejected, handleRejected)

            // Delete Contact
            .addCase(deleteContact.pending, handlePending)
            .addCase(deleteContact.fulfilled, (state, action) => {
                state.loading = false;
                state.contacts = state.contacts.filter(c => c._id !== action.payload);
            })
            .addCase(deleteContact.rejected, handleRejected)

            // Tags
            .addCase(addTags.pending, handlePending)
            .addCase(addTags.fulfilled, (state, action) => {
                state.loading = false;
                updateContactInList(state, action.payload);
            })
            .addCase(addTags.rejected, handleRejected)

            .addCase(removeTags.pending, handlePending)
            .addCase(removeTags.fulfilled, (state, action) => {
                state.loading = false;
                updateContactInList(state, action.payload);
            })
            .addCase(removeTags.rejected, handleRejected)

            // Notes
            .addCase(addNotes.pending, handlePending)
            .addCase(addNotes.fulfilled, (state, action) => {
                state.loading = false;
                updateContactInList(state, action.payload);
            })
            .addCase(addNotes.rejected, handleRejected)

            .addCase(removeNotes.pending, handlePending)
            .addCase(removeNotes.fulfilled, (state, action) => {
                state.loading = false;
                updateContactInList(state, action.payload);
            })
            .addCase(removeNotes.rejected, handleRejected)

            .addCase(updateNote.pending, handlePending)
            .addCase(updateNote.fulfilled, (state, action) => {
                state.loading = false;
                updateContactInList(state, action.payload);
            })
            .addCase(updateNote.rejected, handleRejected)
            
            // Assign Contact
            .addCase(assignContact.pending, handlePending)
            .addCase(assignContact.fulfilled, (state, action) => {
                state.loading = false;
                // Backend returns { success: true, message: "...", data: updatedContact }
                if (action.payload.data) {
                    updateContactInList(state, action.payload.data);
                }
            })
            .addCase(assignContact.rejected, handleRejected);
    },
});

export const { updateChatPreview, clearContacts, setSelectedContact, clearSelectedContact } = contactSlice.actions;
export default contactSlice.reducer;
